import { parseFeedbackWithPositions } from "@bandwith/lezer-feedback";
import { LyricsAnchorChip } from "@bandwith/ui";
import { type ReactNode, useMemo } from "react";
import { RecordingLink, type RecordingName } from "./RecordingLink";
import type { PlayableRecording } from "./types";

type Props = {
  body: string;
  recordings: PlayableRecording[];
  /**
   * What a recording chip calls its take: the filename on a song page, where
   * the song is already known, or the song title on a setlist page.
   * Defaults to the filename.
   */
  recordingName?: RecordingName;
  /** Called when a lyrics anchor chip is clicked */
  onLyricsAnchorClick?: (anchorText: string) => void;
};

/**
 * Parses feedback body text and renders:
 * - Recording references as clickable play links: [Recording Name @ 1:30]
 * - Lyrics anchors as clickable highlight chips: {@"lyrics text"}
 *
 * Uses the Lezer grammar-based parser for robust markup handling.
 */
export function FeedbackBody({
  body,
  recordings,
  recordingName = "filename",
  onLyricsAnchorClick,
}: Props) {
  const elements = useMemo(() => parseFeedbackWithPositions(body), [body]);

  // If no elements, return plain text
  if (elements.length === 0) {
    return <span>{body}</span>;
  }

  const result: ReactNode[] = elements.map((el) => {
    switch (el.type) {
      case "text":
        return el.content;
      case "recording":
        return (
          <RecordingLink
            key={`rec-${el.from}`}
            recordingName={el.name}
            timestamp={el.timestamp}
            recordings={recordings}
            displayName={recordingName}
          />
        );
      case "lyrics":
        return (
          <LyricsAnchorChip
            key={`lyrics-${el.from}`}
            anchorText={el.anchorText}
            onClick={onLyricsAnchorClick}
          />
        );
      default: {
        const _exhaustive: never = el;
        return _exhaustive;
      }
    }
  });

  return <span className="whitespace-pre-wrap">{result}</span>;
}
